// src/pages/suppliers/components/StockManagement.jsx

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  Paper,
  LinearProgress,
  Alert,
  Snackbar,
  Slide,
  InputAdornment,
  Tooltip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useMediaQuery,
  useTheme,
  alpha,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Inventory2 as Inventory2Icon,
  Warehouse as WarehouseIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { BRAND_COLORS } from '../buyers/components/styles';

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const API_ENDPOINTS = {
  STOCK: {
    UPDATE: `${API_BASE_URL}/products/stock.php`,
    HISTORY: (productId) => `${API_BASE_URL}/products/stock.php?action=history&product_id=${productId}`,
    LOW_STOCK: (threshold = 10) => `${API_BASE_URL}/products/stock.php?action=low_stock&threshold=${threshold}`,
    SUMMARY: `${API_BASE_URL}/products/stock.php?action=summary`,
  },
};

// ============================================
// CONSTANTS
// ============================================

const PRODUCT_STATUSES = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
  DRAFT: 'draft',
  OUT_OF_STOCK: 'out_of_stock',
};

const STATUS_COLORS = {
  [PRODUCT_STATUSES.ACTIVE]: BRAND_COLORS.success,
  [PRODUCT_STATUSES.INACTIVE]: BRAND_COLORS.error,
  [PRODUCT_STATUSES.PENDING]: BRAND_COLORS.warning,
  [PRODUCT_STATUSES.DRAFT]: BRAND_COLORS.info,
  [PRODUCT_STATUSES.OUT_OF_STOCK]: '#EF4444',
};

const STATUS_LABELS = {
  [PRODUCT_STATUSES.ACTIVE]: 'Active',
  [PRODUCT_STATUSES.INACTIVE]: 'Inactive',
  [PRODUCT_STATUSES.PENDING]: 'Pending',
  [PRODUCT_STATUSES.DRAFT]: 'Draft',
  [PRODUCT_STATUSES.OUT_OF_STOCK]: 'Out of Stock',
};

const STOCK_ACTIONS = {
  ADD: 'add',
  SUBTRACT: 'subtract',
  SET: 'set',
};

const STOCK_ACTION_LABELS = {
  [STOCK_ACTIONS.ADD]: 'Add Stock',
  [STOCK_ACTIONS.SUBTRACT]: 'Remove Stock',
  [STOCK_ACTIONS.SET]: 'Set Stock',
};

const STOCK_ACTION_ICONS = {
  [STOCK_ACTIONS.ADD]: <TrendingUpIcon />,
  [STOCK_ACTIONS.SUBTRACT]: <TrendingDownIcon />,
  [STOCK_ACTIONS.SET]: <EditIcon />,
};

const STOCK_ACTION_COLORS = {
  [STOCK_ACTIONS.ADD]: BRAND_COLORS.success,
  [STOCK_ACTIONS.SUBTRACT]: BRAND_COLORS.error,
  [STOCK_ACTIONS.SET]: BRAND_COLORS.primary,
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[STOCK-MGMT] ${message}`, data || ''),
  error: (message, error) => console.error(`[STOCK-MGMT] ${message}`, error || ''),
  debug: (message, data) => console.debug(`[STOCK-MGMT] ${message}`, data || ''),
  warn: (message, data) => console.warn(`[STOCK-MGMT] ${message}`, data || ''),
};

// ============================================
// STYLED COMPONENTS
// ============================================

const StockCard = styled(Paper)(({ theme, variant }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  border: '1px solid #E5E7EB',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: variant === 'primary' 
    ? `linear-gradient(135deg, ${BRAND_COLORS.primary} 0%, ${BRAND_COLORS.primaryDark} 100%)`
    : variant === 'success'
    ? `linear-gradient(135deg, ${BRAND_COLORS.success} 0%, #059669 100%)`
    : variant === 'warning'
    ? `linear-gradient(135deg, ${BRAND_COLORS.warning} 0%, #D97706 100%)`
    : variant === 'error'
    ? `linear-gradient(135deg, ${BRAND_COLORS.error} 0%, #DC2626 100%)`
    : '#FFFFFF',
  color: variant ? '#FFFFFF' : 'inherit',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 32px ${alpha(BRAND_COLORS.primary, 0.1)}`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}));

const StockChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.7rem',
  borderRadius: 6,
  height: 28,
  backgroundColor: status ? alpha(status, 0.12) : alpha(BRAND_COLORS.info, 0.12),
  color: status || BRAND_COLORS.info,
  '& .MuiChip-icon': {
    fontSize: 16,
    color: 'inherit',
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  fontSize: '0.7rem',
  borderRadius: 6,
  height: 24,
  backgroundColor: status ? alpha(status, 0.12) : alpha(BRAND_COLORS.info, 0.12),
  color: status || BRAND_COLORS.info,
  '& .MuiChip-icon': {
    fontSize: 14,
    color: 'inherit',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.85rem',
  padding: '12px 16px',
  borderBottom: '1px solid #F0F2F5',
  '&.MuiTableCell-head': {
    backgroundColor: '#F8FAFC',
    color: BRAND_COLORS.text.secondary,
    fontWeight: 700,
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '0.5px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 10px',
    fontSize: '0.75rem',
  },
}));

// ============================================
// STOCK MANAGEMENT DIALOG
// ============================================

const StockManagementDialog = ({ 
  open, 
  onClose, 
  product, 
  onStockUpdate,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [action, setAction] = useState(STOCK_ACTIONS.ADD);
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setAction(STOCK_ACTIONS.ADD);
      setQuantity('');
      setNote('');
      setError('');
      setSuccess(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    // Validate quantity
    const qty = parseInt(quantity);
    if (isNaN(qty) || qty <= 0) {
      setError('Please enter a valid quantity greater than 0');
      return;
    }

    if (action === STOCK_ACTIONS.SUBTRACT && qty > (product?.quantity_in_stock || 0)) {
      setError(`Cannot remove ${qty} units. Only ${product?.quantity_in_stock || 0} units in stock.`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // ✅ FIX: Get supplier_id from product data (already validated by parent)
      // This matches the pattern in ProductManagement.jsx
      const supplierId = product?.supplier_id;
      
      if (!supplierId) {
        throw new Error('Supplier ID not found. Please refresh and try again.');
      }

      const payload = {
        product_id: product.id,
        quantity: qty,
        action: action,
        supplier_id: supplierId, // ✅ Pass supplier_id explicitly
        note: note || `${STOCK_ACTION_LABELS[action]} ${qty} units`,
      };

      logger.info(`Updating stock for product ${product.id}:`, payload);

      // ✅ FIX: Don't send Authorization header if not needed
      // Or include it but also pass supplier_id in body
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Try to get token but don't fail if not available
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(API_ENDPOINTS.STOCK.UPDATE, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      logger.debug('Stock update response:', result);

      if (result.success) {
        setSuccess(true);
        // Update parent component
        if (onStockUpdate && result.data) {
          onStockUpdate({
            ...product,
            quantity_in_stock: result.data.new_stock || qty,
            status: result.data.status || product.status,
          });
        }
        // Close after a delay to show success
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to update stock');
      }
    } catch (error) {
      logger.error('Stock update error:', error);
      setError(error.message || 'Failed to update stock. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [quantity, action, product, onStockUpdate, onClose]);

  const getStockStatus = useCallback((currentStock) => {
    if (currentStock === 0) return { label: 'Out of Stock', color: BRAND_COLORS.error };
    if (currentStock < 10) return { label: 'Low Stock', color: BRAND_COLORS.warning };
    if (currentStock < 50) return { label: 'In Stock', color: BRAND_COLORS.success };
    return { label: 'Well Stocked', color: BRAND_COLORS.primary };
  }, []);

  const stockStatus = product ? getStockStatus(product.quantity_in_stock || 0) : { label: 'Unknown', color: BRAND_COLORS.info };

  // Calculate max for input
  const maxQuantity = action === STOCK_ACTIONS.SUBTRACT 
    ? (product?.quantity_in_stock || 0) 
    : 99999;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 20,
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 2, sm: 2.5 },
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarehouseIcon sx={{ color: BRAND_COLORS.primary }} />
          <Typography variant="h6" fontWeight={700}>
            Manage Stock
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {product && (
          <>
            {/* Product Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight={600}>
                Product
              </Typography>
              <Typography variant="body1" fontWeight={700} noWrap>
                {product.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="textSecondary">
                  CAS: {product.registryNumber || 'N/A'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  SKU: {product.id}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Supplier ID: {product.supplier_id || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {/* Current Stock */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                Current Stock
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h4" fontWeight={800}>
                  {product.quantity_in_stock || 0}
                </Typography>
                <StockChip
                  label={stockStatus.label}
                  status={stockStatus.color}
                  size="small"
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((product.quantity_in_stock || 0) / 100 * 100, 100)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mt: 1,
                  bgcolor: alpha(stockStatus.color, 0.1),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: stockStatus.color,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Stock Action */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                Stock Action
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(STOCK_ACTION_LABELS).map(([key, label]) => (
                  <Grid item xs={4} key={key}>
                    <Button
                      fullWidth
                      variant={action === key ? 'contained' : 'outlined'}
                      onClick={() => setAction(key)}
                      startIcon={STOCK_ACTION_ICONS[key]}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        py: 1,
                        ...(action === key && {
                          bgcolor: STOCK_ACTION_COLORS[key],
                          '&:hover': {
                            bgcolor: STOCK_ACTION_COLORS[key],
                            opacity: 0.9,
                          },
                        }),
                        ...(action !== key && {
                          borderColor: STOCK_ACTION_COLORS[key],
                          color: STOCK_ACTION_COLORS[key],
                          '&:hover': {
                            borderColor: STOCK_ACTION_COLORS[key],
                            bgcolor: alpha(STOCK_ACTION_COLORS[key], 0.04),
                          },
                        }),
                      }}
                    >
                      {isMobile ? (key === STOCK_ACTIONS.ADD ? 'Add' : key === STOCK_ACTIONS.SUBTRACT ? 'Remove' : 'Set') : label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Quantity Input */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                Quantity
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (e.target.value === '' || val >= 0) {
                    setQuantity(e.target.value);
                    setError('');
                  }
                }}
                placeholder="Enter quantity..."
                InputProps={{
                  startAdornment: action === STOCK_ACTIONS.SUBTRACT ? (
                    <InputAdornment position="start">
                      <RemoveIcon sx={{ color: BRAND_COLORS.error }} />
                    </InputAdornment>
                  ) : action === STOCK_ACTIONS.ADD ? (
                    <InputAdornment position="start">
                      <AddIcon sx={{ color: BRAND_COLORS.success }} />
                    </InputAdornment>
                  ) : null,
                  endAdornment: action === STOCK_ACTIONS.SUBTRACT && (
                    <InputAdornment position="end">
                      <Typography variant="caption" color="textSecondary">
                        Max: {maxQuantity}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                error={!!error}
                helperText={error}
                disabled={loading}
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* Note Input */}
            <Box>
              <Typography variant="subtitle2" color="textSecondary" fontWeight={600} gutterBottom>
                Note (Optional)
              </Typography>
              <TextField
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note about this stock change..."
                disabled={loading}
                size="small"
                multiline
                rows={2}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {success && (
              <Alert
                severity="success"
                icon={<CheckIcon />}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Stock updated successfully!
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          borderTop: '1px solid #E5E7EB',
          gap: 1,
          bgcolor: '#F8FAFC',
          flexWrap: 'wrap',
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            color: BRAND_COLORS.text.primary,
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !quantity || success}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: STOCK_ACTION_COLORS[action] || BRAND_COLORS.primary,
            '&:hover': {
              bgcolor: STOCK_ACTION_COLORS[action] || BRAND_COLORS.primaryDark,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              bgcolor: alpha(BRAND_COLORS.primary, 0.3),
            },
          }}
        >
          {loading ? 'Updating...' : `${STOCK_ACTION_LABELS[action]} ${quantity || ''} Units`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// STOCK HISTORY DIALOG
// ============================================

const StockHistoryDialog = ({ open, onClose, product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch history when dialog opens
  useEffect(() => {
    if (open && product) {
      fetchHistory();
    }
  }, [open, product]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // ✅ FIX: Pass supplier_id in URL as well
      const supplierId = product?.supplier_id;
      let url = API_ENDPOINTS.STOCK.HISTORY(product.id);
      
      if (supplierId) {
        url += `&supplier_id=${supplierId}`;
      }

      const headers = {};
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        headers: headers,
      });
      const result = await response.json();
      if (result.success) {
        setHistory(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch history');
      }
    } catch (error) {
      logger.error('Error fetching stock history:', error);
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [product]);

  const getActionColor = useCallback((action) => {
    switch (action) {
      case 'add': return BRAND_COLORS.success;
      case 'subtract': return BRAND_COLORS.error;
      case 'set': return BRAND_COLORS.primary;
      default: return BRAND_COLORS.info;
    }
  }, []);

  const getActionLabel = useCallback((action) => {
    switch (action) {
      case 'add': return 'Added';
      case 'subtract': return 'Removed';
      case 'set': return 'Set';
      default: return action;
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 20,
          overflow: 'hidden',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 2, sm: 2.5 },
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <HistoryIcon sx={{ color: BRAND_COLORS.primary }} />
          <Typography variant="h6" fontWeight={700}>
            Stock History
          </Typography>
          {product && (
            <Chip
              label={product.name}
              size="small"
              sx={{ bgcolor: alpha(BRAND_COLORS.primary, 0.08) }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Loading history...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Inventory2Icon sx={{ fontSize: 48, color: '#D1D5DB' }} />
            <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
              No stock history available
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Stock changes will appear here once you update inventory
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date & Time</StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                  <StyledTableCell align="center">Quantity</StyledTableCell>
                  <StyledTableCell align="center">Previous</StyledTableCell>
                  <StyledTableCell align="center">New</StyledTableCell>
                  <StyledTableCell>Note</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((entry, index) => (
                  <TableRow key={entry.id || index} hover>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {entry.date || entry.created_at || 'N/A'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={getActionLabel(entry.action)}
                        size="small"
                        sx={{
                          bgcolor: alpha(getActionColor(entry.action), 0.1),
                          color: getActionColor(entry.action),
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2" fontWeight={700}>
                        {entry.quantity || 0}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2" color="textSecondary">
                        {entry.previous || 0}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="body2" fontWeight={700}>
                        {entry.new || 0}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 150 }}>
                        {entry.note || '—'}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #E5E7EB' }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// MAIN STOCK MANAGEMENT COMPONENT
// ============================================

const StockManagement = ({ 
  products = [], 
  onStockUpdate, 
  onRefresh,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Memoized filtered products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(term) ||
        p.registryNumber?.toLowerCase().includes(term) ||
        p.productType?.toLowerCase().includes(term)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }

    return filtered;
  }, [products, searchTerm, filterStatus]);

  // Pagination
  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  const totalProducts = filteredProducts.length;

  // Handlers
  const handleOpenStockDialog = useCallback((product) => {
    setSelectedProduct(product);
    setStockDialogOpen(true);
  }, []);

  const handleCloseStockDialog = useCallback(() => {
    setStockDialogOpen(false);
    setSelectedProduct(null);
  }, []);

  const handleOpenHistoryDialog = useCallback((product) => {
    setSelectedProduct(product);
    setHistoryDialogOpen(true);
  }, []);

  const handleCloseHistoryDialog = useCallback(() => {
    setHistoryDialogOpen(false);
    setSelectedProduct(null);
  }, []);

  const handleStockUpdate = useCallback((updatedProduct) => {
    if (onStockUpdate) {
      onStockUpdate(updatedProduct);
    }
    setSnackbar({
      open: true,
      message: `Stock updated for ${updatedProduct.name}`,
      severity: 'success',
    });
  }, [onStockUpdate]);

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleFilterChange = useCallback((event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setPage(0);
  }, []);

  // Stock status helpers
  const getStockStatus = useCallback((quantity) => {
    if (quantity === 0 || quantity === null) {
      return { label: 'Out of Stock', color: BRAND_COLORS.error, icon: <WarningIcon /> };
    }
    if (quantity < 10) {
      return { label: 'Low Stock', color: BRAND_COLORS.warning, icon: <WarningIcon /> };
    }
    if (quantity < 50) {
      return { label: 'In Stock', color: BRAND_COLORS.success, icon: <CheckIcon /> };
    }
    return { label: 'Well Stocked', color: BRAND_COLORS.primary, icon: <CheckIcon /> };
  }, []);

  const getStockColor = useCallback((quantity) => {
    if (quantity === 0 || quantity === null) return '#EF4444';
    if (quantity < 10) return '#F59E0B';
    if (quantity < 50) return '#10B981';
    return BRAND_COLORS.primary;
  }, []);

  // Snackbar close handler
  const handleSnackbarClose = useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);

  // Render loading state
  if (loading && products.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
          Loading stock data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarehouseIcon sx={{ color: BRAND_COLORS.primary }} />
            Stock Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {totalProducts} products • {products.reduce((sum, p) => sum + (p.quantity_in_stock || 0), 0)} total units
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} sx={{ bgcolor: '#FFFFFF', boxShadow: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          alignItems: 'center',
        }}
      >
        <TextField
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          sx={{
            flex: { xs: '1 1 100%', sm: '1 1 200px' },
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={filterStatus}
            onChange={handleFilterChange}
            displayEmpty
            sx={{ borderRadius: 3 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="out_of_stock">Out of Stock</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
          {totalProducts} products
        </Typography>
      </Box>

      {/* Stock Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StockCard>
            <Typography variant="caption" color="textSecondary">
              Total Products
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {products.length}
            </Typography>
          </StockCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StockCard variant="success">
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              In Stock
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {products.filter(p => (p.quantity_in_stock || 0) > 0).length}
            </Typography>
          </StockCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StockCard variant="warning">
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Low Stock (&lt;10)
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {products.filter(p => (p.quantity_in_stock || 0) > 0 && (p.quantity_in_stock || 0) < 10).length}
            </Typography>
          </StockCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StockCard variant="error">
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Out of Stock
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {products.filter(p => (p.quantity_in_stock || 0) === 0).length}
            </Typography>
          </StockCard>
        </Grid>
      </Grid>

      {/* Products Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Product</StyledTableCell>
                <StyledTableCell>CAS Number</StyledTableCell>
                <StyledTableCell align="center">Stock</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Inventory2Icon sx={{ fontSize: 48, color: '#D1D5DB' }} />
                      <Typography variant="h6" color="textSecondary">
                        No products found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm ? 'Try adjusting your search' : 'Products will appear here once registered'}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ) : (
                paginatedProducts.map((product) => {
                  const stock = product.quantity_in_stock || 0;
                  const stockStatus = getStockStatus(stock);
                  const statusColor = STATUS_COLORS[product.status] || BRAND_COLORS.info;
                  const isOutOfStock = stock === 0;

                  return (
                    <TableRow
                      key={product.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(BRAND_COLORS.primary, 0.02),
                        },
                        ...(isOutOfStock && {
                          backgroundColor: alpha('#EF4444', 0.02),
                        }),
                      }}
                    >
                      <StyledTableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {product.name}
                          </Typography>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" fontFamily="monospace" noWrap>
                          {product.registryNumber || 'N/A'}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{ color: getStockColor(stock) }}
                          >
                            {stock}
                          </Typography>
                          <Chip
                            label={stockStatus.label}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.6rem',
                              bgcolor: alpha(stockStatus.color, 0.1),
                              color: stockStatus.color,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip
                          label={STATUS_LABELS[product.status] || 'Active'}
                          status={statusColor}
                          size="small"
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Manage Stock">
  <Button
    size="small"
    variant="outlined"
    onClick={() => handleOpenStockDialog(product)}
    startIcon={<WarehouseIcon />}
    sx={{
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.75rem',
      borderColor: BRAND_COLORS.primary,
      color: BRAND_COLORS.primary,
      '&:hover': {
        borderColor: BRAND_COLORS.primaryDark,
        bgcolor: alpha(BRAND_COLORS.primary, 0.04),
      },
    }}
  >
    Manage Stock
  </Button>
</Tooltip>
                          <Tooltip title="View History">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenHistoryDialog(product)}
                              sx={{
                                color: BRAND_COLORS.info,
                                bgcolor: alpha(BRAND_COLORS.info, 0.04),
                                '&:hover': {
                                  bgcolor: alpha(BRAND_COLORS.info, 0.12),
                                },
                              }}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalProducts > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderTop: '1px solid #E5E7EB',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
            }}
          >
            <Typography variant="body2" color="textSecondary">
              Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, totalProducts)} of {totalProducts}
            </Typography>
            <TablePagination
              component="div"
              count={totalProducts}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiToolbar-root': {
                  px: 0,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Stock Management Dialog */}
      <StockManagementDialog
        open={stockDialogOpen}
        onClose={handleCloseStockDialog}
        product={selectedProduct}
        onStockUpdate={handleStockUpdate}
      />

      {/* Stock History Dialog */}
      <StockHistoryDialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        product={selectedProduct}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          icon={snackbar.severity === 'success' ? <CheckIcon /> : <WarningIcon />}
          sx={{
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ============================================
// EXPORTS
// ============================================

export default StockManagement;
export { StockManagementDialog, StockHistoryDialog };